import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { useLang } from "../context/LanguageContext";
import { T, pick } from "../lib/translations";
import { ALL_CITIES, ALL_GUIDES } from "../lib/relatedContent";
import { getPosts, prefetchPost, getBootPosts } from "../lib/blogCache";
import { ArrowRight, BookOpen, Calendar, Clock, MapPin, Phone } from "lucide-react";

const decodeHtml = (str) => {
  if (!str) return "";
  const el = document.createElement("textarea");
  el.innerHTML = str;
  return el.value;
};

const stripTags = (html) => {
  if (!html) return "";
  const el = document.createElement("div");
  el.innerHTML = html;
  return (el.textContent || el.innerText || "").trim();
};

export default function OffensesPage() {
  const { lang } = useLang();
  const isHi = lang === "hi";
  const [posts, setPosts] = useState(() => getBootPosts() || []);
  const [loading, setLoading] = useState(() => !getBootPosts());

  useEffect(() => {
    getPosts().then((p) => {
      setPosts(p);
      setLoading(false);
    });
  }, []);

  // Group posts by their WordPress category, biggest groups first
  const groups = useMemo(() => {
    const map = new Map();
    posts.forEach((p) => {
      const cat = p.category || "General";
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat).push(p);
    });
    return [...map.entries()].sort((a, b) => b[1].length - a[1].length);
  }, [posts]);

  const anchorFor = (i) => `offense-group-${i}`;

  return (
    <>
      <SEO
        title={
          isHi
            ? "अपराध के अनुसार ज़मानत गाइड्स — Rihai"
            : "Bail Guides by Offence — Rihai"
        }
        description={
          isHi
            ? "हर धारा और अपराध के लिए सरल भाषा में ज़मानत गाइड्स — 302, 307, 498A, NDPS और अधिक।"
            : "Plain-language bail guides organised by offence and section — 302, 307, 498A, NDPS and more. Written for families, not lawyers."
        }
        path="/offenses"
      />
      <Nav />
      <main className="pt-32 pb-20">
        <div className="container-x">
          <div className="max-w-2xl mb-10">
            <div className="overline mb-5">{isHi ? "अपराध के अनुसार" : "Browse by offence"}</div>
            <h1
              className="font-serif-display serif-textured text-5xl sm:text-6xl font-light leading-tight mb-5"
              data-testid="offenses-h1"
            >
              {isHi
                ? "जो धारा लगी है, उसी की गाइड पढ़िए।"
                : "Read the guide for the exact section you're facing."}
            </h1>
            <p className="text-lg text-[#4A4640] leading-relaxed">
              {isHi
                ? "हर अपराध की ज़मानत अलग होती है। नीचे अपनी धारा ढूँढिए — सरल भाषा में, बिना डराए।"
                : "Bail is different for every offence. Find your section below — plain language, no fear."}
            </p>
          </div>

          {/* Category chips */}
          {!loading && groups.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-14" data-testid="offense-chips">
              {groups.map(([cat, list], i) => (
                <button
                  key={cat}
                  onClick={() =>
                    document.getElementById(anchorFor(i))?.scrollIntoView({ behavior: "smooth", block: "start" })
                  }
                  className="rounded-full border border-[#D8CDBA] px-4 py-2 text-sm text-[#4A4640] hover:border-[#2C4C3B] hover:text-[#2C4C3B] transition-colors"
                  data-testid={`offense-chip-${i}`}
                >
                  {cat} <span className="text-[#736F66]">({list.length})</span>
                </button>
              ))}
            </div>
          )}

          {loading ? (
            <div className="space-y-14" data-testid="offenses-loading">
              {[0, 1].map((s) => (
                <div key={s} className="animate-pulse">
                  <div className="h-8 w-64 bg-[#EAE6DF] rounded mb-6" />
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {[0, 1, 2].map((c) => (
                      <div key={c} className="rounded-2xl border border-[#D8CDBA] p-6">
                        <div className="h-5 w-3/4 bg-[#EAE6DF] rounded mb-3" />
                        <div className="h-4 w-full bg-[#EAE6DF] rounded mb-2" />
                        <div className="h-4 w-2/3 bg-[#EAE6DF] rounded" />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : groups.length === 0 ? (
            <div className="text-[#736F66]" data-testid="offenses-empty">
              {isHi ? "अभी कोई गाइड नहीं। जल्द ही देखिए।" : "No guides yet. Check back soon."}
            </div>
          ) : (
            groups.map(([cat, list], i) => (
              <section key={cat} id={anchorFor(i)} className="mb-16 scroll-mt-28" data-testid={`offense-group-${i}`}>
                <div className="flex items-baseline gap-3 mb-6">
                  <h2 className="font-serif-display text-3xl sm:text-4xl font-medium leading-tight">
                    {cat}
                  </h2>
                  <span className="text-sm text-[#736F66]">
                    {list.length} {isHi ? "गाइड" : list.length === 1 ? "guide" : "guides"}
                  </span>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {list.map((p) => (
                    <Link
                      key={p.id}
                      to={`/blog/${p.slug}`}
                      onMouseEnter={() => prefetchPost(p.slug)}
                      className="group bg-white border border-[#D8CDBA] rounded-2xl p-6 hover:-translate-y-0.5 hover:shadow-lg transition-all"
                      data-testid={`offense-card-${p.id}`}
                    >
                      <h3 className="font-serif-display text-xl font-medium text-[#1A1A1A] leading-snug mb-3 group-hover:text-[#2C4C3B] transition-colors">
                        {decodeHtml(p.title)}
                      </h3>
                      <p className="text-sm text-[#4A4640] leading-relaxed line-clamp-2 mb-4">
                        {stripTags(p.excerpt).slice(0, 140)}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="text-xs text-[#736F66] flex items-center gap-3">
                          <span className="inline-flex items-center gap-1"><Calendar size={12} /> {p.date}</span>
                          <span className="inline-flex items-center gap-1"><Clock size={12} /> {p.read_time}</span>
                        </div>
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-[#2C4C3B] group-hover:gap-2 transition-all">
                          {pick(T.blog.read, lang)} <ArrowRight size={14} />
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            ))
          )}

          {/* Internal links — guides + cities */}
          <div className="mt-20 pt-12 border-t border-[#D8CDBA]" data-testid="offenses-guides-strip">
            <div className="overline mb-5">
              {isHi ? "पहले समझिए — Rihai गाइड्स" : "Understand first — the Rihai Guides"}
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {ALL_GUIDES.map((g) => (
                <Link
                  key={g.slug}
                  to={`/guide/${g.slug}`}
                  className="group bg-white border border-[#D8CDBA] rounded-2xl p-5 hover:-translate-y-0.5 hover:shadow-lg transition-all"
                  data-testid={`offenses-guide-${g.slug}`}
                >
                  <BookOpen size={16} className="text-[#C46B2F] mb-3" strokeWidth={1.75} />
                  <div className="font-serif-display text-lg font-medium text-[#1A1A1A] leading-snug group-hover:text-[#2C4C3B] transition-colors">
                    {pick(g.title, lang)}
                  </div>
                </Link>
              ))}
            </div>
          </div>
          <div className="mt-12" data-testid="offenses-cities-strip">
            <div className="overline mb-4">
              {isHi ? "शहर के अनुसार सत्यापित वकील" : "Verified bail lawyers by city"}
            </div>
            <div className="flex flex-wrap gap-2">
              {ALL_CITIES.map((ct) => (
                <Link
                  key={ct.slug}
                  to={`/${ct.slug}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#D8CDBA] px-4 py-2 text-sm text-[#4A4640] hover:border-[#2C4C3B] hover:text-[#2C4C3B] transition-colors"
                  data-testid={`offenses-city-${ct.slug}`}
                >
                  <MapPin size={12} strokeWidth={2} /> {isHi ? ct.cityHindi : ct.city}
                </Link>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="mt-20 rounded-3xl bg-[#2C4C3B] text-white p-10 md:p-14 relative overflow-hidden" data-testid="offenses-cta">
            <div className="grain" />
            <div className="tricolor-band tricolor-band-thick mb-8 max-w-[120px]" />
            <h3 className="font-serif-display text-3xl md:text-4xl font-light leading-tight mb-4 relative max-w-2xl">
              {isHi ? "धारा पता चल गई? अब अगला क़दम।" : "Found your section? Now the next step."}
            </h3>
            <p className="text-[#D8CDBA] max-w-lg mb-8 relative">
              {isHi
                ? "एक सत्यापित ज़मानत वकील एक कॉल दूर है — कोई बिचौलिया नहीं।"
                : "A verified bail lawyer is one call away — no middlemen."}
            </p>
            <div className="flex flex-wrap gap-3 relative">
              <Link to="/#lead" className="btn-primary bg-white text-[#2C4C3B] hover:bg-[#EAE6DF]" data-testid="offenses-cta-lead">
                {pick(T.hero.ctaPrimary, lang)} <ArrowRight size={16} />
              </Link>
              <a
                href={`tel:${T.phone.number}`}
                className="btn-secondary border-white text-white hover:bg-white hover:text-[#2C4C3B]"
                data-testid="offenses-cta-call"
              >
                <Phone size={16} strokeWidth={2} /> {T.phone.display}
              </a>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
